import { Table } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { KeyedRow } from '../../components/KeyedRow';
import { PageHeader } from '../../components/PageHeader';
import { Paper } from '../../components/Paper';
import { Title } from '../../components/Title';
import { useFilter } from '../../hooks/useFilter';
import { usePagination } from '../../hooks/usePagination';
import { queryWithFilter } from '../../util/api/queryWithFilter';
import { queryWithFilterAndPages } from '../../util/api/queryWithFilterAndPages';
import { formatToCurrency, formatToDateString } from '../../util/formatting';
import { undefinedCheck } from '../../util/undefinedCheck';
import { ExportButton } from '../export/ExportButton';
import { EodAreaLogsChart } from './components/EodLogsAreaChart';
import type { EodLogChartLineDto, EodLogDto } from './types/EodLogDto';

export function EodLogs() {
	const { organisation } = useParams();
	const filter = useFilter();
	const pagination = usePagination();

	const eodLogsQuery = queryWithFilterAndPages<EodLogDto[]>(
		`${organisation}/eod-logs/all`,
		filter.value,
		pagination.value,
	);

	const eodLogsCountQuery = queryWithFilter<number>(
		`${organisation}/eod-logs/count`,
		filter.value,
	);

	const eodChartQuery = queryWithFilter<EodLogChartLineDto[]>(
		`${organisation}/eod-logs/chart-single`,
		filter.value,
	);

	return (
		<>
			<PageHeader
				title={'EOD Logs'}
				filter={filter}
				filterOnSameLine
				rightContent={
					<ExportButton apiPath={`${organisation}/eod-logs/export`} />
				}
			/>
			<Paper p={45}>
				<EodAreaLogsChart
					lines={eodChartQuery.data?.value}
					isLoading={eodChartQuery.isLoading || !eodChartQuery.data}
				/>
			</Paper>
			<DataTable
				pagination={pagination}
				totalItems={eodLogsCountQuery.data?.value ?? 100}
				isRowsLoading={eodLogsQuery.isLoading}
				isPaginationLoading={eodLogsCountQuery.isLoading}
				rows={pagination.value.pageSize}
				minHeight={'44vh'}
				maxHeight={'55vh'}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Title label={'Date'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Report No.'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Till'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Transactions'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Gross Sales'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Net Sales'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Discounts'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Refunds'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Verifier'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Branch'} />
						</Table.Th>
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{eodLogsQuery.data?.value.map((eodLog) => (
						<KeyedRow>
							<Table.Td>{formatToDateString(eodLog.dateTime)}</Table.Td>
							<Table.Td>{undefinedCheck(eodLog.reportNo)}</Table.Td>
							<Table.Td>{undefinedCheck(eodLog.till)}</Table.Td>
							<Table.Td>{eodLog.transactionCount}</Table.Td>
							<Table.Td>{formatToCurrency(eodLog.grossSales)}</Table.Td>
							<Table.Td>{formatToCurrency(eodLog.netSales)}</Table.Td>
							<Table.Td>{formatToCurrency(eodLog.totalDiscounts)}</Table.Td>
							<Table.Td>{formatToCurrency(eodLog.refunds)}</Table.Td>
							<Table.Td>{undefinedCheck(eodLog.verifier)}</Table.Td>
							<Table.Td>{undefinedCheck(eodLog.branch)}</Table.Td>
						</KeyedRow>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}
